// Inpainting Page Logic
import { ImageTransformControlsManager } from './utils/image-transform-controls.js';
import type { ImageTransform } from './utils/image-transform.js';
import { loadAuthenticatedMedia } from './utils/authenticated-media-loader.js';
import {
  getStoredTransformSource,
  clearStoredTransformSource,
} from './utils/transform-source.js';

// Global state
let backgroundImage: HTMLImageElement | null = null;
let backgroundTransformManager: ImageTransformControlsManager | null = null;

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let maskCanvas: HTMLCanvasElement | null = null;
let maskCtx: CanvasRenderingContext2D | null = null;
let isDrawing = false;
let undoStack: ImageData[] = [];

export function initInpaintingPage() {
  // Get DOM elements
  const backgroundUploadArea = document.getElementById('backgroundUploadArea');
  const backgroundInput = document.getElementById(
    'backgroundInput'
  ) as HTMLInputElement;

  // Check for sessionStorage data (from detail page)
  const stored = getStoredTransformSource();

  if (stored) {
    // Load from sessionStorage
    loadImageFromUrl(stored.source!, stored.prompt, stored.sourceType);

    // Clear sessionStorage after loading
    clearStoredTransformSource();
  }

  // Function to load image from URL (from detail page re-transform)
  async function loadImageFromUrl(
    url: string,
    prompt: string,
    sourceType: string = 'url'
  ) {
    try {
      // Use authenticated media loader for server resources
      const blobUrl = await loadAuthenticatedMedia(url);
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      const filename = url.split('/').pop() || 'image.png';
      const file = new File([blob], filename, { type: blob.type });

      // Set the prompt
      (document.getElementById('prompt') as HTMLTextAreaElement).value = prompt;

      // Trigger background upload with the file
      const fakeEvent = {
        target: {
          files: [file],
        },
      } as any;
      handleBackgroundUpload(fakeEvent);

      // Show success message with source type
      const sourceLabel =
        sourceType === 'source'
          ? 'bron afbeelding'
          : sourceType === 'result'
            ? 'result afbeelding'
            : sourceType === 'input'
              ? 'input afbeelding'
              : 'afbeelding';

      // Wait a bit for the image to load, then show message
      setTimeout(() => {
        showMessage(
          `✅ ${sourceLabel.charAt(0).toUpperCase() + sourceLabel.slice(1)} geladen uit geschiedenis`,
          'success'
        );
      }, 500);
    } catch (error) {
      console.error('Failed to load image from URL:', error);
      showMessage(
        '⚠️ Kon afbeelding niet laden. Probeer handmatig uploaden.',
        'error'
      );
    }
  }

  // Helper function to show messages
  function showMessage(text: string, type: 'success' | 'error' | 'info') {
    const message = document.getElementById('message');
    if (message) {
      const className =
        type === 'success' ? 'success' : type === 'error' ? 'error' : 'info';
      message.innerHTML = `<div class="${className}">${text}</div>`;
    }
  }

  // Upload area click handlers
  backgroundUploadArea?.addEventListener('click', () =>
    backgroundInput.click()
  );

  // File input handlers
  backgroundInput?.addEventListener('change', handleBackgroundUpload);

  // Remove buttons
  document
    .getElementById('removeBackground')
    ?.addEventListener('click', removeBackground);

  // Canvas controls
  document
    .getElementById('brushSize')
    ?.addEventListener('input', updateBrushSize);
  document
    .getElementById('maskOpacity')
    ?.addEventListener('input', updateMaskOpacity);
  document.getElementById('undoBtn')?.addEventListener('click', undo);
  document.getElementById('clearMaskBtn')?.addEventListener('click', clearMask);

  // Form
  document
    .getElementById('inpaintForm')
    ?.addEventListener('submit', handleSubmit);
  document
    .getElementById('newInpaintBtn')
    ?.addEventListener('click', resetPage);

  // Show/hide model selector based on provider
  const providerSelect = document.getElementById(
    'provider'
  ) as HTMLSelectElement;
  const modelSelectGroup = document.getElementById(
    'modelSelectGroup'
  ) as HTMLElement;

  providerSelect?.addEventListener('change', () => {
    if (providerSelect.value === 'replicate') {
      modelSelectGroup.style.display = 'block';
    } else {
      modelSelectGroup.style.display = 'none';
    }
  });

  // Initialize visibility on page load
  if (providerSelect?.value === 'replicate') {
    modelSelectGroup.style.display = 'block';
  }

  // Show/hide FLUX-specific controls based on selected model
  const replicateModelSelect = document.getElementById(
    'replicateModel'
  ) as HTMLSelectElement;
  const outpaintGroup = document.getElementById('outpaintGroup') as HTMLElement;
  const referenceImagesGroup = document.getElementById(
    'referenceImagesGroup'
  ) as HTMLElement;

  function updateFluxControls() {
    const selectedModel = replicateModelSelect.value;

    // Show outpaint for FLUX Fill Pro
    if (selectedModel.includes('flux-fill-pro')) {
      outpaintGroup.style.display = 'block';
    } else {
      outpaintGroup.style.display = 'none';
    }

    // Show reference images for FLUX 2 Max
    if (selectedModel.includes('flux-2-max')) {
      referenceImagesGroup.style.display = 'block';
    } else {
      referenceImagesGroup.style.display = 'none';
    }
  }

  replicateModelSelect?.addEventListener('change', updateFluxControls);

  // Initialize on page load
  if (replicateModelSelect) {
    updateFluxControls();
  }

  // Handle reference image previews
  const referenceImagesInput = document.getElementById(
    'referenceImages'
  ) as HTMLInputElement;
  const referencePreviewContainer = document.getElementById(
    'referencePreviewContainer'
  ) as HTMLElement;
  const referencePreviews = document.getElementById(
    'referencePreviews'
  ) as HTMLElement;

  referenceImagesInput?.addEventListener('change', (e) => {
    const input = e.target as HTMLInputElement;
    const files = input.files;
    if (!files || files.length === 0) {
      referencePreviewContainer.style.display = 'none';
      return;
    }

    referencePreviews.innerHTML = '';
    referencePreviewContainer.style.display = 'block';

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = document.createElement('img');
        img.src = event.target?.result as string;
        img.style.width = '100px';
        img.style.height = '100px';
        img.style.objectFit = 'cover';
        img.style.borderRadius = '4px';
        img.title = file.name;
        referencePreviews.appendChild(img);
      };
      reader.readAsDataURL(file);
    });
  });
}

function handleBackgroundUpload(e: Event) {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (event) => {
    const img = document.getElementById('backgroundImg') as HTMLImageElement;
    img.src = event.target?.result as string;
    backgroundImage = img;

    document.getElementById('backgroundPreview')!.style.display = 'block';
    document.getElementById('backgroundUploadArea')!.style.display = 'none';

    // Initialize transform controls and canvas
    img.onload = () => {
      initBackgroundTransformControls();
      initCanvas();
      document.getElementById('canvasCard')!.style.display = 'block';
      document.getElementById('generateCard')!.style.display = 'block';
    };
  };
  reader.readAsDataURL(file);
}

function initBackgroundTransformControls() {
  if (!backgroundImage) return;

  backgroundTransformManager = new ImageTransformControlsManager({
    containerId: 'backgroundTransformControls',
    imageElement: backgroundImage,
    onTransformChange: (_transform: ImageTransform, _blob: Blob) => {
      // Transform blob not used - we export canvas directly
      // Re-init canvas when transform changes
      if (canvas) {
        initCanvas();
      }
    },
  });
}

function initCanvas() {
  if (!backgroundImage) return;

  canvas = document.getElementById('inpaintingCanvas') as HTMLCanvasElement;
  ctx = canvas.getContext('2d')!;

  // Set canvas to square (1024x1024) for optimal AI model compatibility
  // Image will be drawn with fit:contain to preserve aspect ratio
  const canvasSize = 1024;
  canvas.width = canvasSize;
  canvas.height = canvasSize;

  // Create off-screen canvas for mask
  maskCanvas = document.createElement('canvas');
  maskCanvas.width = canvas.width;
  maskCanvas.height = canvas.height;
  maskCtx = maskCanvas.getContext('2d')!;

  // Clear undo stack on re-init
  undoStack = [];
  (document.getElementById('undoBtn') as HTMLButtonElement).disabled = true;

  // Draw initial image
  renderCanvas();
  renderCanvas();

  // Mouse events
  canvas.addEventListener('mousedown', startDrawing);
  canvas.addEventListener('mousemove', draw);
  canvas.addEventListener('mouseup', stopDrawing);
  canvas.addEventListener('mouseleave', stopDrawing);

  // Touch events for mobile
  canvas.addEventListener('touchstart', handleTouch);
  canvas.addEventListener('touchmove', handleTouch);
  canvas.addEventListener('touchend', stopDrawing);
}

function renderCanvas() {
  if (!canvas || !ctx || !backgroundImage || !maskCanvas) return;

  // Clear
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw background with fit:cover (fill canvas, crop if needed)
  const imgAspect =
    backgroundImage.naturalWidth / backgroundImage.naturalHeight;
  const canvasAspect = canvas.width / canvas.height;

  let drawWidth, drawHeight, drawX, drawY;

  if (imgAspect > canvasAspect) {
    // Image is wider - fit to height, crop sides
    drawHeight = canvas.height;
    drawWidth = drawHeight * imgAspect;
    drawX = (canvas.width - drawWidth) / 2;
    drawY = 0;
  } else {
    // Image is taller or square - fit to width, crop top/bottom
    drawWidth = canvas.width;
    drawHeight = drawWidth / imgAspect;
    drawX = 0;
    drawY = (canvas.height - drawHeight) / 2;
  }

  ctx.drawImage(backgroundImage, drawX, drawY, drawWidth, drawHeight);

  // Draw mask overlay
  const opacityInput = document.getElementById(
    'maskOpacity'
  ) as HTMLInputElement;
  const opacity = parseInt(opacityInput.value) / 100;
  ctx.globalAlpha = opacity;
  ctx.drawImage(maskCanvas, 0, 0);
  ctx.globalAlpha = 1.0;
}

function startDrawing(e: MouseEvent) {
  isDrawing = true;
  draw(e);
}

function draw(e: MouseEvent) {
  if (!isDrawing || !canvas || !maskCtx) return;

  const rect = canvas.getBoundingClientRect();
  const x = ((e.clientX - rect.left) * canvas.width) / rect.width;
  const y = ((e.clientY - rect.top) * canvas.height) / rect.height;

  const brushSizeInput = document.getElementById(
    'brushSize'
  ) as HTMLInputElement;
  const brushSize = parseInt(brushSizeInput.value);

  // Draw on mask canvas
  // IMPORTANT: Draw WHITE (255,255,255) for areas to inpaint
  // The red color is just for visual feedback - we'll convert to white on export
  maskCtx.fillStyle = 'rgba(255, 100, 100, 1)'; // Red mask (visual feedback only)
  maskCtx.beginPath();
  maskCtx.arc(x, y, brushSize / 2, 0, Math.PI * 2);
  maskCtx.fill();

  // Re-render
  renderCanvas();

  // Enable undo
  (document.getElementById('undoBtn') as HTMLButtonElement).disabled = false;
}

function stopDrawing() {
  if (isDrawing && maskCanvas && maskCtx) {
    // Save to undo stack
    undoStack.push(
      maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height)
    );
    if (undoStack.length > 20) undoStack.shift(); // Limit stack size
  }
  isDrawing = false;
}

function handleTouch(e: TouchEvent) {
  e.preventDefault();
  const touch = e.touches[0];
  const mouseEvent = new MouseEvent(
    e.type === 'touchstart' ? 'mousedown' : 'mousemove',
    {
      clientX: touch.clientX,
      clientY: touch.clientY,
    }
  );
  canvas?.dispatchEvent(mouseEvent);
}

function updateBrushSize(e: Event) {
  const input = e.target as HTMLInputElement;
  const valueSpan = document.getElementById('brushSizeValue');
  if (valueSpan) valueSpan.textContent = input.value;
}

function updateMaskOpacity(e: Event) {
  const input = e.target as HTMLInputElement;
  const valueSpan = document.getElementById('maskOpacityValue');
  if (valueSpan) valueSpan.textContent = input.value;
  renderCanvas();
}

function undo() {
  if (undoStack.length > 0 && maskCtx && maskCanvas) {
    const previousState = undoStack.pop()!;
    maskCtx.putImageData(previousState, 0, 0);
    renderCanvas();
    if (undoStack.length === 0) {
      (document.getElementById('undoBtn') as HTMLButtonElement).disabled = true;
    }
  }
}

function clearMask() {
  if (maskCtx && maskCanvas) {
    maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
    renderCanvas();
    undoStack = [];
    (document.getElementById('undoBtn') as HTMLButtonElement).disabled = true;
  }
}

function removeBackground() {
  backgroundImage = null;
  backgroundTransformManager?.destroy();
  backgroundTransformManager = null;
  document.getElementById('backgroundPreview')!.style.display = 'none';
  document.getElementById('backgroundUploadArea')!.style.display = 'block';
  (document.getElementById('backgroundInput') as HTMLInputElement).value = '';
  document.getElementById('canvasCard')!.style.display = 'none';
  document.getElementById('generateCard')!.style.display = 'none';
}

async function handleSubmit(e: Event) {
  e.preventDefault();

  const generateBtn = document.getElementById(
    'generateBtn'
  ) as HTMLButtonElement;
  const message = document.getElementById('message')!;

  // Validate mask
  if (!maskCanvas || !maskCtx) return;
  const maskData = maskCtx.getImageData(
    0,
    0,
    maskCanvas.width,
    maskCanvas.height
  );
  const hasDrawing = maskData.data.some((val) => val > 0);

  if (!hasDrawing) {
    message.innerHTML =
      '<div class="error">❌ Teken eerst een masker op de canvas</div>';
    return;
  }

  generateBtn.disabled = true;
  generateBtn.textContent = '⏳ Generating...';
  message.innerHTML =
    '<div class="info">⏳ Compositing images en generating inpainting...</div>';

  try {
    // Prepare form data
    const formData = new FormData();

    // Export the canvas background layer as the image to send
    // This ensures we send the same cropped/fitted version that the user sees
    const backgroundCanvas = document.createElement('canvas');
    backgroundCanvas.width = canvas!.width;
    backgroundCanvas.height = canvas!.height;
    const backgroundCtx = backgroundCanvas.getContext('2d')!;

    // Redraw the background image with the same fit:cover logic
    if (backgroundImage) {
      const imgAspect =
        backgroundImage.naturalWidth / backgroundImage.naturalHeight;
      const canvasAspect = canvas!.width / canvas!.height;

      let drawWidth, drawHeight, drawX, drawY;

      if (imgAspect > canvasAspect) {
        // Image is wider - fit to height, crop sides
        drawHeight = canvas!.height;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvas!.width - drawWidth) / 2;
        drawY = 0;
      } else {
        // Image is taller or square - fit to width, crop top/bottom
        drawWidth = canvas!.width;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
        drawY = (canvas!.height - drawHeight) / 2;
      }

      backgroundCtx.drawImage(
        backgroundImage,
        drawX,
        drawY,
        drawWidth,
        drawHeight
      );
    }

    // Convert background canvas to blob
    const backgroundBlob = await new Promise<Blob>((resolve, reject) =>
      backgroundCanvas.toBlob((b) => (b ? resolve(b) : reject()), 'image/png')
    );

    formData.append('backgroundImage', backgroundBlob, 'background.png');

    // Convert mask canvas to proper white/black mask
    // Models expect: WHITE (255,255,255) = areas to inpaint, BLACK (0,0,0) = areas to keep
    const properMaskCanvas = document.createElement('canvas');
    properMaskCanvas.width = maskCanvas!.width;
    properMaskCanvas.height = maskCanvas!.height;
    const properMaskCtx = properMaskCanvas.getContext('2d')!;

    // Get mask data and convert red pixels to white
    const maskData = maskCtx!.getImageData(
      0,
      0,
      maskCanvas!.width,
      maskCanvas!.height
    );
    const properMaskData = properMaskCtx.createImageData(
      maskCanvas!.width,
      maskCanvas!.height
    );

    for (let i = 0; i < maskData.data.length; i += 4) {
      // If any color channel is > 0 (red mask drawn), convert to WHITE
      // Otherwise keep BLACK
      if (
        maskData.data[i] > 0 ||
        maskData.data[i + 1] > 0 ||
        maskData.data[i + 2] > 0
      ) {
        properMaskData.data[i] = 255; // R = white
        properMaskData.data[i + 1] = 255; // G = white
        properMaskData.data[i + 2] = 255; // B = white
        properMaskData.data[i + 3] = 255; // A = opaque
      } else {
        properMaskData.data[i] = 0; // R = black
        properMaskData.data[i + 1] = 0; // G = black
        properMaskData.data[i + 2] = 0; // B = black
        properMaskData.data[i + 3] = 255; // A = opaque
      }
    }

    properMaskCtx.putImageData(properMaskData, 0, 0);

    // Convert proper mask canvas to blob
    const maskBlob = await new Promise<Blob>((resolve, reject) =>
      properMaskCanvas.toBlob((b) => (b ? resolve(b) : reject()))
    );
    formData.append('maskImage', maskBlob, 'mask.png');

    // Add form fields
    const promptInput = document.getElementById(
      'prompt'
    ) as HTMLTextAreaElement;
    const providerSelect = document.getElementById(
      'provider'
    ) as HTMLSelectElement;
    const replicateModelSelect = document.getElementById(
      'replicateModel'
    ) as HTMLSelectElement;
    const enhanceCheckbox = document.getElementById(
      'enhance'
    ) as HTMLInputElement;
    const expandMaskCheckbox = document.getElementById(
      'expandMask'
    ) as HTMLInputElement;
    const outpaintModeCheckbox = document.getElementById(
      'outpaintMode'
    ) as HTMLInputElement;
    const referenceImagesInput = document.getElementById(
      'referenceImages'
    ) as HTMLInputElement;

    formData.append('prompt', promptInput.value);
    formData.append('provider', providerSelect.value);

    // Add model parameter if Replicate is selected
    if (providerSelect.value === 'replicate' && replicateModelSelect) {
      formData.append('model', replicateModelSelect.value);
    }

    formData.append('enhance', enhanceCheckbox.checked ? 'true' : 'false');
    formData.append(
      'expandMaskPixels',
      expandMaskCheckbox.checked ? '10' : '0'
    );

    // Add outpaint mode for FLUX Fill Pro
    if (outpaintModeCheckbox && outpaintModeCheckbox.checked) {
      formData.append('outpaintMode', 'true');
    }

    // Add reference images for FLUX 2 Max
    if (referenceImagesInput && referenceImagesInput.files) {
      const refFiles = Array.from(referenceImagesInput.files);
      refFiles.forEach((file, index) => {
        formData.append(`referenceImage${index}`, file);
      });
      formData.append('referenceImageCount', refFiles.length.toString());
    }

    // Submit to API using authenticatedFetch (from auth.js)
    const response = await (window as any).authenticatedFetch('/api/inpaint', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();

    if (response.ok && result.success) {
      // Debug logging
      console.log('Inpainting success! Result:', result);
      console.log('Image URL:', result.imageUrl);

      // Show result
      const resultImg = document.getElementById(
        'resultImg'
      ) as HTMLImageElement;

      // Handle both data URLs and relative paths
      let imageUrl = result.imageUrl;

      // If it's a data URL, use it directly
      if (imageUrl.startsWith('data:')) {
        resultImg.src = imageUrl;
      } else {
        // For server paths, use authenticated media loader
        if (!imageUrl.startsWith('http')) {
          imageUrl = window.location.origin + imageUrl;
        }

        console.log('Fetching authenticated image:', imageUrl);

        try {
          // Use the centralized authenticated media loader
          const blobUrl = await loadAuthenticatedMedia(imageUrl);
          resultImg.src = blobUrl;
          console.log('Image loaded via authenticated media loader');
        } catch (error) {
          console.error('Error loading authenticated image:', error);
          resultImg.src = imageUrl; // Fallback to direct URL
        }
      }

      document.getElementById('resultProvider')!.textContent =
        result.metadata.provider;
      document.getElementById('resultMethod')!.textContent =
        result.metadata.inpaintingMethod;
      document.getElementById('resultTime')!.textContent = `${(
        result.metadata.generationTime / 1000
      ).toFixed(2)}s`;

      document.getElementById('resultsCard')!.style.display = 'block';
      document
        .getElementById('resultsCard')!
        .scrollIntoView({ behavior: 'smooth' });

      message.innerHTML =
        '<div class="success">✅ Inpainting succesvol gegenereerd!</div>';
    } else {
      console.error('Inpainting failed:', result);
      message.innerHTML = `<div class="error">❌ ${result.message || 'Er ging iets mis'}</div>`;
    }
  } catch (error) {
    console.error('Inpainting error:', error);
    console.error('Error details:', JSON.stringify(error, null, 2));
    message.innerHTML =
      '<div class="error">❌ Er ging iets mis bij het genereren. Probeer het opnieuw.</div>';
  } finally {
    generateBtn.disabled = false;
    generateBtn.textContent = '✨ Genereer Inpainting Result';
  }
}

function resetPage() {
  location.reload();
}

// Make functions available globally for inline event handlers
(window as any).initInpaintingPage = initInpaintingPage;
